/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSeaSurveyApi } from "~/api/GeneratedApi";
import DraggableDialog from "~/components/DraggableDialog";
import { setSelectedPointcloud } from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import { useAccess } from "~/utils/hooks/useAccess";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { updateTilesetSettings } from "../../functions/setTilesetSettings";
import { Custom3DTileset } from "../../types/CesiumMapItems";
import AttenuationAdjustment from "./AttenuationAdjustment";
import { HeightOffsetAdjustment } from "./HeightOffsetAdjustment";
import PointColorAdjustment from "./PointColorAdjustment";
import PointSizeAdjustment from "./PointSizeAdjustment";

interface Props {
  tilesets: Custom3DTileset[];
  open: boolean;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
}));

const CesiumTilesetAdjustment = ({ tilesets, open }: Props): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("cesium");
  const { updateFileCesiumSettings, updateSurveyOffset } = useSeaSurveyApi();
  const { SeaSurveyUpdate } = useAccess();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [elevationSettings, setElevationSettings] = React.useState<string>();
  const [intensitySettings, setIntensitySettings] = React.useState<string>();
  const [pointSizeSettings, setPointSizeSettings] = React.useState<string>();
  const [heightOffset, setHeightOffset] = React.useState<number>();

  const [attenuationSettings, setAttenuationSettings] =
    React.useState<string>();
  const [staticColor, setStaticColor] = React.useState<string>();
  const [colorSettings, setColorSettings] = React.useState<string>();
  const [coloringType, setColoringType] = React.useState<string>();
  const apiErrorHandler = useApiErrorHandler();

  const handleSave = React.useCallback(() => {
    const promises: Promise<string>[] = [];
    if (heightOffset !== undefined)
      promises.push(
        updateSurveyOffset(
          { additionalHeightOffset: heightOffset },
          tilesets[0].seaSurveyId,
        ),
      );
    tilesets.forEach((ts) => {
      promises.push(
        updateFileCesiumSettings(
          {
            attenuationSettings,
            colorSettings,
            coloringType,
            elevationSettings,
            intensitySettings,
            pointSizeSettings,
            staticColor,
          },
          ts.seaSurveyId,
          ts.fileId,
        ),
      );
    });
    Promise.all(promises)
      .then(() => {
        // Set the new data to the tilesets
        tilesets.forEach((ts) => {
          ts.additionalHeightOffset = heightOffset ?? 0;
          updateTilesetSettings(ts, {
            attenuationSettings,
            colorSettings,
            coloringType,
            elevationSettings,
            intensitySettings,
            pointSizeSettings,
            staticColor,
          });
        });
        enqueueSnackbar(t("general:ok"), { variant: "success" });
      })
      .catch(apiErrorHandler);
  }, [
    apiErrorHandler,
    attenuationSettings,
    colorSettings,
    coloringType,
    elevationSettings,
    enqueueSnackbar,
    heightOffset,
    intensitySettings,
    pointSizeSettings,
    staticColor,
    t,
    tilesets,
    updateFileCesiumSettings,
    updateSurveyOffset,
  ]);

  return (
    <DraggableDialog
      open={open}
      title={t("adjust-pointcloud")}
      toolbar={
        <IconButton
          style={{ marginLeft: "auto" }}
          onClick={() => dispatch(setSelectedPointcloud(undefined))}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      }
      transitionDuration={{ exit: 0 }}
      maxWidth="xs"
      actions={
        SeaSurveyUpdate ? (
          <Button color="secondary" variant="contained" onClick={handleSave}>
            {t("general:save")}
          </Button>
        ) : undefined
      }
    >
      {tilesets.length > 0 ? (
        <Box className={classes.container}>
          <Grid container spacing={2}>
            <HeightOffsetAdjustment
              tilesets={tilesets}
              onHeightOffsetChange={setHeightOffset}
            />
            <PointSizeAdjustment
              tilesets={tilesets}
              onPointSizeSettingsChange={setPointSizeSettings}
            />
            <PointColorAdjustment
              tilesets={tilesets}
              onElevationSettingsChange={setElevationSettings}
              onIntensitySettingsChange={setIntensitySettings}
              onColorSettingsChange={setColorSettings}
              onStaticColorChange={setStaticColor}
              onColoringTypeChange={setColoringType}
            />
            <AttenuationAdjustment
              tilesets={tilesets}
              onAttenuationSettingsChange={setAttenuationSettings}
            />
          </Grid>
        </Box>
      ) : (
        <Typography>{t("pointcloud-not-found")}</Typography>
      )}
    </DraggableDialog>
  );
};

export default CesiumTilesetAdjustment;
